import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { CheckCircle, Trash2 } from 'lucide-react';
import { Table, Modal } from 'antd';
import { toast } from '@rickylandino/react-messages';
import Globals from '../../../config/globals';
import { usePrintHouseMonitoringCount, usePrintHouseMonitoringData, useDismissPrintHouseMonitoring } from '../../../hooks/useWidgetData';
import { getPatientInformationByIDNew } from '../../../common/services/ApiService';
import { printPlainLanguageLetter } from '../../../common/services/ReportService';

const PENDING_PRINT_NOTE =
    'Results pending print will be automatically printed in future by the print house. These results do not require action from you at this time.';

function PrintHouseMonitoringWidget({
    isCollapsed = false,
    isExpanded = false,
    setIsLoading,
    setSharedState,
    queryEnabled = true,
    history
}) {
    const [showPending, setShowPending] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const dataset = showPending ? 'pending-print' : 'requiring-action';
    const shouldLoadFullData = !isCollapsed || isExpanded;

    const { data: countData, isLoading: isLoadingCount } =
        usePrintHouseMonitoringCount((isCollapsed && !isExpanded) && queryEnabled);

    const { data, isLoading, isFetching, isError, error, refetch } =
        usePrintHouseMonitoringData(dataset, currentPage, pageSize, shouldLoadFullData && queryEnabled);

    const rows = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    // Reset to page 1 when dataset toggles
    useEffect(() => {
        setCurrentPage(1);
    }, [showPending]);

    useEffect(() => {
        if (setIsLoading && isExpanded) setIsLoading(isLoading || isFetching);
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    useEffect(() => {
        if (shouldLoadFullData && isError) {
            console.error('Failed to load print house monitoring data:', error);
        }
    }, [shouldLoadFullData, isError, error]);

    useEffect(() => {
        if (setSharedState && refetch) setSharedState(prev => ({ ...prev, refetch }));
    }, [setSharedState, refetch]);

    const formatDate = useCallback((value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    }, []);

    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    const [openingChartId, setOpeningChartId] = useState(null);
    const [showDismissModal, setShowDismissModal] = useState(false);
    const [dismissTarget, setDismissTarget] = useState(null);
    const { mutate: dismissResult, isPending: isDismissing } = useDismissPrintHouseMonitoring();

    const handleDismiss = useCallback((record) => {
        setDismissTarget(record);
        setShowDismissModal(true);
    }, []);

    const handleConfirmDismiss = useCallback(async () => {
        if (!dismissTarget?.patientResultId) return;
        dismissResult(dismissTarget.patientResultId, {
            onSuccess: () => {
                setShowDismissModal(false);
                setDismissTarget(null);
            },
            onError: () => {
                toast.error('Failed to remove from widget. Please try again.');
            }
        });
    }, [dismissTarget, dismissResult]);

    const handleOpenChart = useCallback(async (record) => {
        if (!record.patientId) return;
        setOpeningChartId(record.patientResultId);
        try {
            const data = await getPatientInformationByIDNew(record.patientId);
            history.push({
                pathname: '/patient-dashboard',
                state: { selectedObject: data.patientInformation, examInfo: data.examInfo, patientEthnicity: data.patientEthnicity }
            });
        } catch {
            toast.error('Failed to open patient chart.');
        } finally {
            setOpeningChartId(null);
        }
    }, [history]);

    const accentColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    const mutedColor = Globals.theme === 'dark' ? '#a1a1aa' : '#71717a';

    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 180,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{formatMRN(mrn)}</span>,
            width: 140,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 140,
        },
        {
            title: 'Exam Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            render: (v) => formatDate(v),
            width: 110,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            width: 150,
        },
        {
            title: 'Result Date',
            dataIndex: 'resultDate',
            key: 'resultDate',
            render: (v) => formatDate(v),
            width: 110,
        },
        {
            title: 'BiRAD',
            dataIndex: 'birad',
            key: 'birad',
            width: 75,
        },
        {
            title: 'Recommendation',
            dataIndex: 'recommendation',
            key: 'recommendation',
            width: 180,
        },
        {
            title: 'Est. Print Date',
            dataIndex: 'estimatedDayOfPrint',
            key: 'estimatedDayOfPrint',
            render: (v) => formatDate(v),
            width: 120,
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 265,
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={() => handleOpenChart(record)}
                        disabled={openingChartId === record.patientResultId}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white',
                            border: 'none',
                            cursor: openingChartId === record.patientResultId ? 'default' : 'pointer',
                            opacity: openingChartId === record.patientResultId ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (openingChartId !== record.patientResultId)
                                e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#db2777' : '#be185d';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                        }}
                    >
                        {openingChartId === record.patientResultId ? 'Opening...' : 'Open Chart'}
                    </button>
                    <button
                        onClick={() => printPlainLanguageLetter({
                            birad: record.birad,
                            facilityID: record.facilityId,
                            patientExamID: record.patientExamId,
                            patientResultID: record.patientResultId,
                        })}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5',
                            color: Globals.theme === 'dark' ? '#e4e4e7' : '#3f3f46',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d4d4d8'}`,
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#52525b' : '#e4e4e7';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5';
                        }}
                    >
                        Print Letter
                    </button>
                    <button
                        onClick={() => handleDismiss(record)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d4d4d8'}`,
                            color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#ef4444';
                            e.currentTarget.style.borderColor = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = Globals.theme === 'dark' ? '#a1a1aa' : '#71717a';
                            e.currentTarget.style.borderColor = Globals.theme === 'dark' ? '#52525b' : '#d4d4d8';
                        }}
                        title="Remove from widget"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            ),
        },
    ], [formatDate, formatMRN, handleOpenChart, openingChartId, accentColor, handleDismiss]);

    // COLLAPSED view — count only
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: mutedColor }}
                    />
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div className="text-5xl font-light mb-2" style={{ color: accentColor }}>
                    {count.toLocaleString()}
                </div>
                <div className="text-sm" style={{ color: mutedColor }}>
                    {count === 1 ? 'result requiring print action' : 'results requiring print action'}
                </div>
            </div>
        );
    }

    // Empty state
    if (!isLoading && !isFetching && count === 0 && !showPending) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: accentColor }} />
                <p style={{ color: accentColor }}>No results requiring print action</p>
            </div>
        );
    }

    // EXPANDED view
    return (
        <div className="space-y-3">
            {/* Dataset toggle */}
            <div className="flex items-center gap-3 px-1">
                <label className="flex items-center gap-2 cursor-pointer select-none text-sm" style={{ color: mutedColor }}>
                    <input
                        type="checkbox"
                        checked={showPending}
                        onChange={(e) => setShowPending(e.target.checked)}
                        style={{ accentColor }}
                    />
                    Show Results Pending Print
                </label>
            </div>

            {/* Pending-print informational note */}
            {showPending && (
                <div
                    className="px-3 py-2 rounded text-sm"
                    style={{
                        backgroundColor: Globals.theme === 'dark' ? '#3f3f46' : '#fdf4ff',
                        border: `1px solid ${Globals.theme === 'dark' ? '#71717a' : '#e879f9'}`,
                        color: Globals.theme === 'dark' ? '#e4e4e7' : '#4b5563'
                    }}
                >
                    {PENDING_PRINT_NOTE}
                </div>
            )}

            {/* Dataset label */}
            <div className="text-xs font-semibold uppercase tracking-wide px-1" style={{ color: accentColor }}>
                {showPending ? 'Results Pending Print' : 'Results Requiring Action'}
            </div>

            <Table
                columns={columns}
                dataSource={rows}
                rowKey={(record) => record.patientResultId}
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalCount,
                    onChange: (page, newPageSize) => {
                        setCurrentPage(page);
                        if (newPageSize !== pageSize) {
                            setPageSize(newPageSize);
                            setCurrentPage(1);
                        }
                    },
                    pageSizeOptions: ['25', '50', '100', '200'],
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} results`,
                }}
                size="small"
                scroll={{ y: 500 }}
                virtual
                loading={(isLoading && !isExpanded) || isFetching}
            />

            <Modal
                visible={showDismissModal}
                title="Remove from Widget"
                onCancel={() => { setShowDismissModal(false); setDismissTarget(null); }}
                footer={[
                    <button
                        key="cancel"
                        onClick={() => { setShowDismissModal(false); setDismissTarget(null); }}
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d1d5db'}`,
                            cursor: 'pointer',
                            marginRight: 8
                        }}
                    >
                        Cancel
                    </button>,
                    <button
                        key="confirm"
                        onClick={handleConfirmDismiss}
                        disabled={isDismissing}
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                        Remove
                    </button>
                ]}
            >
                <p>
                    Removing <strong>{dismissTarget?.patientName}</strong> will hide this result from the print house monitoring widget.
                </p>
                <p>Are you sure?</p>
            </Modal>
        </div>
    );
}

export default withRouter(PrintHouseMonitoringWidget);
